import * as React from 'react';
import { observer } from 'mobx-react';
import { IOptimizerControlParams } from './IOptimizerControlParams';
import { OptimizerTutorial } from './OptimizerTutorial/OptimizerTutorial';
import { OptimizerViewButtons } from './OptimizerViewButtons/OptimizerViewButtons';
import { OptimizerTrajectoryTypeButtons } from './OptimizerTrajectoryTypeButtons/OptimizerTrajectoryTypeButtons';
import { OptimizerDataViewControl } from './../OptimizerDataViewControl/OptimizerDataViewControl';
import { OptimizerTrajectoryViewControl } from './../OptimizerTrajectoryViewControl/OptimizerTrajectoryViewControl';
import { Parameter, OptimizationHelper } from 'MeasurementsCalculations';
import {
  IOptimizationType,
  IReportOptimization,
  IOptimizerHighlight,
  IOptimizerBar,
  ITrajectoryMeasurement,
  IReportMeasurement,
  IReportMeasurementGroup,
  ReportKind,
} from 'Data';
export interface IOptimizerControlState {
  shouldRerender: boolean;
}

@observer
export class OptimizerControl extends React.Component<
  IOptimizerControlParams,
  IOptimizerControlState
> {
  private showOptimizerTutorial: boolean = false;
  private optimizedMeasurementsData: IReportOptimization | undefined;
  private trajectoryMeasurement: ITrajectoryMeasurement | undefined;
  private driverOptimizedTypes: Array<IOptimizationType>;
  private ironOptimizedTypes: Array<IOptimizationType>;
  private selectedStrokeId: Array<string>;
  private selectedStrokeIdsForAverage: Array<string>;
  private optimizedTypes: Array<IOptimizationType>;
  private optimizerHighlights: Array<IOptimizerHighlight>;
  private optimizationBars: Array<IOptimizerBar>;

  constructor(props: IOptimizerControlParams) {
    super(props, undefined);

    this.state = { ...this.state, ...{ shouldRerender: false } };
    const carry: IOptimizationType = {
      title: 'Carry',
      value: 'carry',
    };
    const total: IOptimizationType = {
      title: 'Total',
      value: 'total',
    };
    const combined: IOptimizationType = {
      title: 'Carry & Total',
      value: 'combined',
    };
    this.driverOptimizedTypes = [carry, total, combined];
    const high: IOptimizationType = {
      title: 'High Trajectory',
      value: 'high',
    };
    const medium: IOptimizationType = {
      title: 'Mid Trajectory',
      value: 'medium',
    };
    const low: IOptimizationType = {
      title: 'Low Trajectory',
      value: 'low',
    };
    this.ironOptimizedTypes = [high, medium, low];
    this.optimizerHighlights = [];
    this.optimizationBars = [];
    this.changeOptimizationType = this.changeOptimizationType.bind(this);
    if (this.props.clubType === 'Driver') {
      this.optimizedTypes = this.driverOptimizedTypes;
      if (
        this.props.selectedOptimizedType &&
        this.optimizedTypes.filter(x => x.value === this.props.selectedOptimizedType.value)
          .length === 0
      )
        this.changeOptimizationType(carry);
    } else {
      this.optimizedTypes = this.ironOptimizedTypes;
      if (
        this.props.selectedOptimizedType &&
        this.optimizedTypes.filter(x => x.value === this.props.selectedOptimizedType.value)
          .length === 0
      )
        this.changeOptimizationType(high);
    }
    this.selectedStrokeId = [];
    this.selectedStrokeIdsForAverage = [];
    this.loadData(this.props);
  }

  findSelectedStrokeId(): void {
    const result: string[] = [];
    if (
      this.props.selectedStroke &&
      this.props.selectedStroke.isVisible &&
      this.props.selectedStroke.id
    ) {
      result.push(this.props.selectedStroke.id);
    }
    this.selectedStrokeId = result;
  }

  findSelectedAverageIds(
    selectedAverage: IReportMeasurement | undefined,
    selectedGroupOfStrokes: IReportMeasurementGroup | undefined
  ): string[] {
    const result: string[] = [];
    if (selectedAverage && selectedGroupOfStrokes) {
      if (OptimizationHelper.strokeCanBeOptimized(selectedAverage)) {
        selectedGroupOfStrokes.normalizedStrokes
          .filter(x => {
            return x.isVisible;
          })
          .forEach(stroke => stroke.id && result.push(stroke.id));
      }
    }
    return result;
  }

  changeOptimizationType(newOptimizationType: IOptimizationType) {
    this.props.selectOptimizedType(newOptimizationType);
  }

  componentWillReceiveProps(nextProps: IOptimizerControlParams) {
    var selectedUnitIsNotTheSame =
      this.props.unitPresentation.unitSystem &&
      nextProps.unitPresentation.unitSystem &&
      this.props.unitPresentation.unitSystem !== nextProps.unitPresentation.unitSystem;
    if (selectedUnitIsNotTheSame) {
      this.setState({ shouldRerender: false }, () => this.loadData(nextProps));
      return;
    }
    var selectedOptimizationTypeIsSame =
      this.props.selectedOptimizedType &&
      nextProps.selectedOptimizedType &&
      this.props.selectedOptimizedType.value === nextProps.selectedOptimizedType.value;

    if (selectedOptimizationTypeIsSame) {
      //if stroke is not change return, do not load data
      if (
        this.props.selectedStroke &&
        nextProps.selectedStroke &&
        this.props.selectedStroke.id === nextProps.selectedStroke.id
      ) {
        return;
      } //if selectedAverange and same strokes is taken in account for calculation averange - not load data
      else if (this.props.selectedAverage && nextProps.selectedAverage) {
        const nextStrokes =
          nextProps.selectedGroupOfStrokes && nextProps.selectedGroupOfStrokes.strokes;
        const strokes =
          this.props.selectedGroupOfStrokes && this.props.selectedGroupOfStrokes.strokes;

        if (
          nextStrokes &&
          strokes &&
          nextStrokes.length === strokes.length &&
          nextStrokes.every((el, index) => el.id === strokes[index].id)
        ) {
          return;
        }
      }
    }

    this.setState({ shouldRerender: false }, () => this.loadData(nextProps));
  }

  setOptimizedMeasurementsData(data: IReportOptimization) {
    this.optimizedMeasurementsData = data;

    if (this.optimizedMeasurementsData !== null) {
      let stroke = null;
      if (this.props.selectedStroke) stroke = this.props.selectedStroke;
      else if (this.props.selectedAverage) stroke = this.props.selectedAverage;

      if (stroke) {
        this.trajectoryMeasurement = {
          carry: this.props.unitPresentation.value(Parameter.Carry, stroke.carry),
          maxHeight: this.props.unitPresentation.value(Parameter.Height, stroke.height),
          total: this.props.unitPresentation.value(Parameter.Total, stroke.total),
          trajectory: stroke.trajectory,
          lowerTrajectory: this.optimizedMeasurementsData.lowerTrajectory,
          upperTrajectory: this.optimizedMeasurementsData.upperTrajectory,
        };
      }
    }
  }

  addOptimizationBar(param: Parameter, lower?: number, upper?: number, value?: number) {
    this.optimizationBars.push({
      parameterName: this.props.unitPresentation.title(param),
      values: {
        lower: this.props.unitPresentation.value(param, lower),
        upper: this.props.unitPresentation.value(param, upper),
        value: this.props.unitPresentation.value(param, value),
      },
      unit: this.props.unitPresentation.displayUnit(param),
    });
  }

  loadData(newProps?: IOptimizerControlParams) {
    let props = { ...newProps, ...this.props };
    this.findSelectedStrokeId();
    this.selectedStrokeIdsForAverage = this.findSelectedAverageIds(
      props && props.selectedAverage,
      props && props.selectedGroupOfStrokes
    );
    let selectedStrokes: string[] = [];
    if (props.selectedStroke) {
      selectedStrokes = this.selectedStrokeId;
    } else if (props.selectedAverage) {
      selectedStrokes = this.selectedStrokeIdsForAverage;
    }

    let selectedOptimizedType = props.selectedOptimizedType;

    if (props.clubType === 'Driver') {
      if (
        this.driverOptimizedTypes.filter(x => x.value === selectedOptimizedType.value).length ===
          0 &&
        selectedOptimizedType.value !== this.driverOptimizedTypes[0].value
      ) {
        this.changeOptimizationType(this.driverOptimizedTypes[0]);
        selectedOptimizedType = this.driverOptimizedTypes[0];
      }
      this.optimizedTypes = this.driverOptimizedTypes;

      if (
        (props.selectedAverage != null && this.selectedStrokeIdsForAverage.length !== 0) ||
        (this.selectedStrokeId != null &&
          this.selectedStrokeId.length !== 0 &&
          OptimizationHelper.strokeCanBeOptimized(props.selectedStroke))
      ) {
        props.reportDataService
          .getOptimizationData(selectedStrokes, selectedOptimizedType['value'], props.reportId)
          .done(data => {
            this.optimizerHighlights = [];
            if (
              selectedOptimizedType['value'] === 'carry' ||
              selectedOptimizedType['value'] === 'combined'
            ) {
              if (props.selectedStroke != null) {
                this.optimizerHighlights.push({
                  title: props.unitPresentation.title(Parameter.Carry),
                  highlightValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Carry, props.selectedStroke.carry) * 10
                    ) / 10,
                  optimalValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Carry, data.optimalCarry) * 10
                    ) / 10,
                  unit: props.unitPresentation.displayUnit(Parameter.Carry),
                });
              } else if (props.selectedAverage != null) {
                this.optimizerHighlights.push({
                  title: props.unitPresentation.title(Parameter.Carry),
                  highlightValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Carry, props.selectedAverage.carry) *
                        10
                    ) / 10,
                  optimalValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Carry, data.optimalCarry) * 10
                    ) / 10,
                  unit: props.unitPresentation.displayUnit(Parameter.Carry),
                });
              }
            }
            if (
              selectedOptimizedType['value'] === 'total' ||
              selectedOptimizedType['value'] === 'combined'
            ) {
              if (props.selectedStroke != null) {
                this.optimizerHighlights.push({
                  title: props.unitPresentation.title(Parameter.Total),
                  highlightValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Total, props.selectedStroke.total) * 10
                    ) / 10,
                  optimalValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Total, data.optimalTotal) * 10
                    ) / 10,
                  unit: props.unitPresentation.displayUnit(Parameter.Total),
                });
              } else if (props.selectedAverage != null) {
                this.optimizerHighlights.push({
                  title: props.unitPresentation.title(Parameter.Total),
                  highlightValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Total, props.selectedAverage.total) *
                        10
                    ) / 10,
                  optimalValue:
                    Math.round(
                      props.unitPresentation.value(Parameter.Total, data.optimalTotal) * 10
                    ) / 10,
                  unit: props.unitPresentation.displayUnit(Parameter.Total),
                });
              }
            }
            this.optimizationBars = [];
            //Spin Loft
            if (props.selectedStroke && props.selectedStroke.spinLoft) {
              this.addOptimizationBar(
                Parameter.SpinLoft,
                data.spinLoft.lower,
                data.spinLoft.upper,
                props.selectedStroke.spinLoft
              );
            } else if (props.selectedAverage && props.selectedAverage.spinLoft) {
              this.addOptimizationBar(
                Parameter.SpinLoft,
                data.spinLoft.lower,
                data.spinLoft.upper,
                props.selectedAverage.spinLoft
              );
            }

            //Ball Speed
            if (props.selectedStroke && props.selectedStroke.ballSpeed) {
              this.addOptimizationBar(
                Parameter.BallSpeed,
                data.ballSpeed.lower,
                data.ballSpeed.upper,
                props.selectedStroke.ballSpeed
              );
            } else if (props.selectedAverage && props.selectedAverage.ballSpeed) {
              this.addOptimizationBar(
                Parameter.BallSpeed,
                data.ballSpeed.lower,
                data.ballSpeed.upper,
                props.selectedAverage.ballSpeed
              );
            }

            //Launch Angle
            if (props.selectedStroke && props.selectedStroke.launchAngle) {
              this.addOptimizationBar(
                Parameter.LaunchAngle,
                data.launchAngle.lower,
                data.launchAngle.upper,
                props.selectedStroke.launchAngle
              );
            } else if (props.selectedAverage && props.selectedAverage.launchAngle) {
              this.addOptimizationBar(
                Parameter.LaunchAngle,
                data.launchAngle.lower,
                data.launchAngle.upper,
                props.selectedAverage.launchAngle
              );
            }

            //Spin Rate
            if (props.selectedStroke && props.selectedStroke.spinRate) {
              this.addOptimizationBar(
                Parameter.SpinRate,
                data.spinRate.lower,
                data.spinRate.upper,
                props.selectedStroke.spinRate
              );
            } else if (props.selectedAverage && props.selectedAverage.spinRate) {
              this.addOptimizationBar(
                Parameter.SpinRate,
                data.spinRate.lower,
                data.spinRate.upper,
                props.selectedAverage.spinRate
              );
            }

            //Height
            if (props.selectedStroke && props.selectedStroke.height) {
              this.addOptimizationBar(
                Parameter.Height,
                data.height.lower,
                data.height.upper,
                props.selectedStroke.height
              );
            } else if (props.selectedAverage && props.selectedAverage.height) {
              this.addOptimizationBar(
                Parameter.Height,
                data.height.lower,
                data.height.upper,
                props.selectedAverage.height
              );
            }

            this.setOptimizedMeasurementsData(data);
            this.setState({ shouldRerender: true });
          });
      }
    } else if (props.clubType) {
      if (
        this.ironOptimizedTypes.filter(x => x.value === selectedOptimizedType.value).length === 0 &&
        selectedOptimizedType.value !== this.ironOptimizedTypes[0].value
      ) {
        this.changeOptimizationType(this.ironOptimizedTypes[0]);
        selectedOptimizedType = this.ironOptimizedTypes[0];
      }
      this.optimizedTypes = this.ironOptimizedTypes;

      if (
        (props.selectedAverage != null && this.selectedStrokeIdsForAverage.length !== 0) ||
        (props.selectedStroke != null &&
          this.selectedStrokeId.length !== 0 &&
          OptimizationHelper.strokeCanBeOptimized(props.selectedStroke))
      ) {
        props.reportDataService
          .getOptimizationData(selectedStrokes, selectedOptimizedType['value'], props.reportId)
          .done(data => {
            this.optimizerHighlights = [];
            if (props.selectedStroke != null) {
              this.optimizerHighlights.push({
                title: props.unitPresentation.title(Parameter.Carry),
                highlightValue:
                  Math.round(
                    props.unitPresentation.value(Parameter.Carry, props.selectedStroke.carry) * 10
                  ) / 10,
                optimalValue:
                  Math.round(
                    props.unitPresentation.value(Parameter.Carry, data.optimalCarry) * 10
                  ) / 10,
                unit: props.unitPresentation.displayUnit(Parameter.Carry),
              });
            } else if (props.selectedAverage != null) {
              this.optimizerHighlights.push({
                title: props.unitPresentation.title(Parameter.Carry),
                highlightValue:
                  Math.round(
                    props.unitPresentation.value(Parameter.Carry, props.selectedAverage.carry) * 10
                  ) / 10,
                optimalValue:
                  Math.round(
                    props.unitPresentation.value(Parameter.Carry, data.optimalCarry) * 10
                  ) / 10,
                unit: props.unitPresentation.displayUnit(Parameter.Carry),
              });
            }
            this.optimizationBars = [];

            //Attack angle
            if (props.selectedStroke && props.selectedStroke.attackAngle) {
              this.addOptimizationBar(
                Parameter.AttackAngle,
                data.attackAngle.lower,
                data.attackAngle.upper,
                props.selectedStroke.attackAngle
              );
            } else if (props.selectedAverage && props.selectedAverage.attackAngle) {
              this.addOptimizationBar(
                Parameter.AttackAngle,
                data.attackAngle.lower,
                data.attackAngle.upper,
                props.selectedAverage.attackAngle
              );
            }

            //Spin Loft
            if (props.selectedStroke && props.selectedStroke.spinLoft) {
              this.addOptimizationBar(
                Parameter.SpinLoft,
                data.spinLoft.lower,
                data.spinLoft.upper,
                props.selectedStroke.spinLoft
              );
            } else if (props.selectedAverage && props.selectedAverage.spinLoft) {
              this.addOptimizationBar(
                Parameter.SpinLoft,
                data.spinLoft.lower,
                data.spinLoft.upper,
                props.selectedAverage.spinLoft
              );
            }

            //Ball Speed
            if (props.selectedStroke && props.selectedStroke.ballSpeed) {
              this.addOptimizationBar(
                Parameter.BallSpeed,
                data.ballSpeed.lower,
                data.ballSpeed.upper,
                props.selectedStroke.ballSpeed
              );
            } else if (props.selectedAverage && props.selectedAverage.ballSpeed) {
              this.addOptimizationBar(
                Parameter.BallSpeed,
                data.ballSpeed.lower,
                data.ballSpeed.upper,
                props.selectedAverage.ballSpeed
              );
            }

            //Launch Angle
            if (props.selectedStroke && props.selectedStroke.launchAngle) {
              this.addOptimizationBar(
                Parameter.LaunchAngle,
                data.launchAngle.lower,
                data.launchAngle.upper,
                props.selectedStroke.launchAngle
              );
            } else if (props.selectedAverage && props.selectedAverage.launchAngle) {
              this.addOptimizationBar(
                Parameter.LaunchAngle,
                data.launchAngle.lower,
                data.launchAngle.upper,
                props.selectedAverage.launchAngle
              );
            }

            //Spin Rate
            if (props.selectedStroke && props.selectedStroke.spinRate) {
              this.addOptimizationBar(
                Parameter.SpinRate,
                data.spinRate.lower,
                data.spinRate.upper,
                props.selectedStroke.spinRate
              );
            } else if (props.selectedAverage && props.selectedAverage.spinRate) {
              this.addOptimizationBar(
                Parameter.SpinRate,
                data.spinRate.lower,
                data.spinRate.upper,
                props.selectedAverage.spinRate
              );
            }

            //Height
            if (props.selectedStroke && props.selectedStroke.height) {
              this.addOptimizationBar(
                Parameter.Height,
                data.height.lower,
                data.height.upper,
                props.selectedStroke.height
              );
            } else if (props.selectedAverage && props.selectedAverage.height) {
              this.addOptimizationBar(
                Parameter.Height,
                data.height.lower,
                data.height.upper,
                props.selectedAverage.height
              );
            }

            this.setOptimizedMeasurementsData(data);
            this.setState({ shouldRerender: true });
          });
      }
    }
  }

  renderInfoBoxes() {
    //console.log("this.props.selectedStroke", this.props.selectedStroke);
    //console.log("this.selectedStrokeI", this.selectedStrokeId);
    //console.log("canOptimize", this.props.selectedStroke.canOptimize);

    if (!this.props.showNormalizedData) {
      return this.renderInfoBox(
        'Optimizer is not being shown if the normalized data is not selected.',
        'Check the normalized data to see the optimizer.'
      );
    }
    if (
      this.props.reportKind === ReportKind.multiGroupReport ||
      this.props.reportKind === ReportKind.singleGroupReport ||
      this.props.reportKind === ReportKind.combineTestReport ||
      this.props.reportKind === ReportKind.testCenterReport
    ) {
      if (this.props.selectedStrokesGroups.length === 0)
        return this.renderInfoBox(
          'You have no groups selected.',
          'Select a group and a shot to activate the optimizer.'
        );

      if (!this.props.selectedStroke && !this.props.selectedAverage)
        return this.renderInfoBox(
          'You have 0 strokes selected.',
          'Select a stroke or average to see the optimizer.'
        );

      if (!this.props.clubType)
        return this.renderInfoBox(
          'The club type is not specified for this stroke.',
          'Optimizer view not available.'
        );

      if (
        this.props.selectedAverage &&
        (!this.selectedStrokeIdsForAverage ||
          (this.selectedStrokeIdsForAverage && this.selectedStrokeIdsForAverage.length === 0))
      )
        return this.renderInfoBox(
          'The average of the group cannot be shown.',
          'Select another stroke or average to see the optimizer.'
        );

      if (
        this.props.selectedStroke &&
        (!this.selectedStrokeId || (this.selectedStrokeId && this.selectedStrokeId.length === 0))
      )
        return this.renderInfoBox(
          'The stroke cannot be optimized.',
          'Choose another stroke or average to see the optimizer.'
        );

      if (this.props.selectedStroke && !this.props.selectedStroke.canOptimize) {
        return this.renderInfoBox(
          'The stroke cannot be optimized.',
          'There is not enough information to show the optimizer for the shot.'
        );
      }
    }

    if (
      this.props.reportKind === ReportKind.singleShotReport ||
      this.props.reportKind === ReportKind.puttingSingleShotReport
    ) {
      if (!this.props.clubType)
        return this.renderInfoBox(
          'The club type is not specified for this stroke.',
          'Optimizer view not available.'
        );

      if (this.props.selectedStroke && !this.props.selectedStroke.canOptimize) {
        return this.renderInfoBox(
          'The stroke cannot be optimized.',
          'There is not enough information to show the optimizer for the shot.'
        );
      }
    }

    return null;
  }

  renderInfoBox(line1: string, line2: string) {
    return (
      <div className="info-box-container hidden-print">
        <div className="container-with-empty-tiles">
          <div className="tile"></div>
          <div className="tile"></div>
        </div>
        <div className="container-with-empty-optimizer"></div>
        <div className="info-container">
          <p>{line1}</p>
          <p>{line2}</p>
        </div>
      </div>
    );
  }

  render() {
    let optimizerTransparency =
      this.props.showOptimizerTutorial && this.props.isWeb ? 'optimizer-opacity' : '';
    let infoBoxes = this.renderInfoBoxes();

    return (
      <div className="OptimizerControl">
        {this.props.isWeb && this.props.showOptimizerTutorial === true && (
          <OptimizerTutorial
            isWeb={this.props.isWeb}
            showOptimizerTutorial={this.props.showOptimizerTutorial}
            hideOptimizerTutorial={this.props.hideOptimizerTutorial}
            doNotShowOptimizerAgain={this.props.doNotShowOptimizerAgain}
          ></OptimizerTutorial>
        )}
        <div className={optimizerTransparency}>
          <div className="row">
            <OptimizerTrajectoryTypeButtons
              selectedOptimizedType={this.props.selectedOptimizedType}
              optimizedTypes={this.optimizedTypes}
              changeOptimizationType={this.changeOptimizationType}
              clubType={this.props.clubType}
            ></OptimizerTrajectoryTypeButtons>
            <OptimizerViewButtons
              selectedOptimizerView={this.props.selectedOptimizerView}
              selectOptimizerView={this.props.selectOptimizerView}
            ></OptimizerViewButtons>
          </div>
          {infoBoxes}

          {!infoBoxes && this.state.shouldRerender && this.props.selectedOptimizerView === 'Data' && (
            <OptimizerDataViewControl
              optimizationBars={this.optimizationBars}
              optimizerHighlights={this.optimizerHighlights}
              measurementsData={this.props.selectedStroke}
              unitPresentation={this.props.unitPresentation}
              clubType={this.props.clubType}
              selectedGroupOfStrokes={this.props.selectedGroupOfStrokes}
              selectedAverage={this.props.selectedAverage}
              selectedStroke={this.props.selectedStroke}
              reportKind={this.props.reportKind}
              showOptimizerTutorial={this.props.showOptimizerTutorial}
              isWeb={this.props.isWeb}
            >
              {this.state.shouldRerender}
            </OptimizerDataViewControl>
          )}

          {!infoBoxes &&
            this.state.shouldRerender &&
            this.props.selectedOptimizerView === 'Trajectory' && (
              <OptimizerTrajectoryViewControl
                optimizedData={this.trajectoryMeasurement}
                unitPresentation={this.props.unitPresentation}
                selectedStroke={this.props.selectedStroke}
                selectedGroupOfStrokes={this.props.selectedGroupOfStrokes}
                selectedAverage={this.props.selectedAverage}
                selectedClub={this.props.clubType}
                selectedStrokesGroups={this.props.selectedStrokesGroups}
                reportKind={this.props.reportKind}
              ></OptimizerTrajectoryViewControl>
            )}
        </div>
      </div>
    );
  }
}
